import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { Global } from "@emotion/react";

import AssignmentEmbedContainer from "@/assignments/AssignmentEmbedContainer";
import { useAssignmentsGet } from "@/assignments/useAssignmentsGet/useAssignmentsGet";
import useAssignmentViewRecordCreate from "@/assignments/useAssignmentViewRecordCreate";
import useLazyAssignmentGet from "@/assignments/useLazyAssignmentGet";
import useReadingLevelGet from "@/assignments/useReadingLevelGet";
import useRecommendedArticleLevelGet from "@/assignments/useRecommendedArticleLevelGet";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import { getLatestAssignment } from "@/assignments/utils/getLatestAssignment";
import {
  UnlockOrPreviewContentBannerForViewVeiledTop,
  UnlockOrPreviewContentBannerForViewVeiledBottom,
} from "@/components/UnlockOrPreviewContentBanner";
import { EN_LANG_ATTRIBUTE_TAG } from "@/constants/Language/Language";
import useHasFeature from "@/features/useHasFeature";
import fetchJson from "@/remoteState/fetchJson";
import ActivityPanel from "@/view/ActivityPanel";
import AssignActionsContainer from "@/view/AssignActionsContainer";
import contentTypes from "@/view/constants/contentTypes";
import ContentAdvisoryBanner from "@/view/ContentAdvisoryBanner";
import ContentDisplay from "@/view/ContentDisplay";
import { articleHasHeaderImage } from "@/view/ContentDisplay/ContentDisplayHeaderMedia/ContentDisplayHeaderMedia.utils";
import ContentHeader from "@/view/ContentHeader";
import ContentLayout from "@/view/ContentLayout";
import ContentSettingsAdvisory from "@/view/ContentSettingsAdvisory";
import LevelAlert from "@/view/LevelAlert";
import PollContainer from "@/view/PollContainer";
import ReadAloudControls from "@/view/ReadAloudControls";
import RelatedContent from "@/view/RelatedContent";
import getMatchingLegacyArticleLevel from "@/view/utils/getMatchingLegacyArticleLevel";
import shouldShowContentAdvisoryBanner from "@/view/utils/shouldShowContentAdvisoryBanner";
import useKeyTerms from "@/view/utils/useKeyTerms";
import useLevelLock from "@/view/utils/useLevelLock";
import useReadingTracker from "@/view/utils/useReadingTracker";
import useSetInitialLevel from "@/view/utils/useSetInitialLevel";
import useShouldShowMobileActions from "@/view/utils/useShouldShowMobileActions";
import useShouldShowPoll from "@/view/utils/useShouldShowPoll";
import useStudentAssignmentEmbedVisibility from "@/view/utils/useStudentAssignmentEmbedVisibility";
import useUpdateLevel from "@/view/utils/useUpdateLevel";
import useVocabularySupport from "@/view/utils/useVocabularySupport";
import ViewPageProviders from "@/view/utils/ViewPageProviders";
import ViewPageBanner from "@/view/ViewPageBanner";
import ViewPageFooter from "@/view/ViewPageFooter";
import { getLicenseTypeFromList } from "@/onboarding/onboarding.utils";
import { USER_ARTICLE_VIEWED_EVENT } from "@/view/ViewPageContent/ViewPageContent.constants";
import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";
import useEventRecordMutation from "@/users/useEventRecordMutation/useEventRecordMutation";
import { logger } from "@/monitoring/utils";
import { recordStudentAssignmentView } from "@/assignments/utils/recordStudentAssignmentView";
import useViewPageContentContext from "./useViewPageContentContext/useViewPageContentContext";
import { globalStyleOverrideCss } from "./ViewPageContent.style";

const propTypes = {
  articleStoriesWithHeaders: PropTypes.array,
  bannerMode: PropTypes.object,
  user: PropTypes.object,
  userProperties: PropTypes.object,
  contentMetadata: PropTypes.object,
  contentMetadataLoading: PropTypes.bool,
  contentMetadataRequestError: PropTypes.object,
};

const ViewPageContent = ({
  articleStoriesWithHeaders,
  bannerMode,
  contentMetadata,
  contentMetadataLoading,
  contentMetadataRequestError,
  user,
  userProperties,
}) => {
  // Router
  const router = useRouter();

  const { content } = useViewPageContentContext();

  // TEMPORARY LOAD TEST SETUP. This code will be removed after we collect enough information on SETI's recommender stability.
  const [isRecommenderLoadExperimentOn] = useHasFeature(
    "recommender_load_test",
  );

  const [isRecommendedArticleLevelExperimentOn] = useHasFeature(
    "recommended_article_level_test",
  );

  useEffect(() => {
    if (isRecommenderLoadExperimentOn) {
      try {
        // Make an API call to SETI and drop the response, because we only care about generating traffic here.
        const requestUrl = `/api/v3/content/recommendations/?shape=card&limit=${10}`;
        fetchJson(requestUrl);
      } catch (_e) {}
    }
  }, [isRecommenderLoadExperimentOn]);

  const { postEventRecord } = useEventRecordMutation();
  const { data: userFeatures } = useUserFeaturesQuery();
  useEffect(() => {
    if (userFeatures?.includes("needs_to_view_articles")) {
      const licenseType = getLicenseTypeFromList(userFeatures);
      postEventRecord({
        event_name: USER_ARTICLE_VIEWED_EVENT,
        data: {
          license_type: licenseType,
          content_id: contentId,
        },
      });
    }
  }, [userFeatures]);

  const { query } = router;
  const { contentId, levelId, redirectUrl, selectedAssignmentId } = query;
  // State
  // Until the user has explicitly set the expanded state, it will be undefined and in the default state for the current screen size.
  // At larger sizes, the panel will be expanded on page load, and at small sizes it will be collapsed.
  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState();

  const { isLiteUser, isStudent, isTeacher } = userProperties;

  // Refs
  const readAloudButtonRef = useRef();
  const mobileActionsToolbarDropdownRef = useRef();

  const matchedLegacyArticleLevel = useMemo(
    () => getMatchingLegacyArticleLevel(articleStoriesWithHeaders, levelId),
    [JSON.stringify(articleStoriesWithHeaders), levelId],
  );

  // Fetching assignments data
  const [
    fetchAssignment,
    { data: selectedAssignment, loading: isSelectedAssignmentLoading },
  ] = useLazyAssignmentGet();
  // This fetches assignment data specific to the article content.
  // We fetch all assignments for a particular user via the AssignmentsProvider,
  // but this allows us to make the assignments for the piece of content available more quickly.
  // This call is more performant as it greatly limits the number of assignments returned.
  const { data: userAssignments, loading: assignmentsLoading } =
    useAssignmentsGet({
      include_drafts: isTeacher,
      story_content_id: contentId,
    });
  const publishedUserAssignments = (userAssignments || []).filter(
    (assignment) => !assignment.is_draft,
  );

  const latestAssignment = getLatestAssignment(publishedUserAssignments);

  useEffect(() => {
    // Fetch the current assignment if the selectedAssignmentId query param exists.
    fetchAssignment({
      id: selectedAssignmentId,
      skip: !getIsSavedAssignment(selectedAssignmentId),
    });
  }, []);

  const [postAssignmentViewRecord] = useAssignmentViewRecordCreate();

  useEffect(() => {
    recordStudentAssignmentView({
      isStudent,
      latestAssignment,
      publishedUserAssignments,
      postAssignmentViewRecord,
    });
  }, [JSON.stringify(publishedUserAssignments)]);

  const [isStudentAssignmentEmbedActive, setIsStudentAssignmentEmbedActive] =
    useStudentAssignmentEmbedVisibility(latestAssignment, userProperties);

  // Banner mode
  const { isBannerModeActive, toggleBannerMode, bannerRef } = bannerMode;
  const showContentAdvisoryBanner = shouldShowContentAdvisoryBanner(
    content,
    userProperties,
  );

  // Hooks
  useReadingTracker({
    articleLevelId: matchedLegacyArticleLevel?.id,
    contentId: levelId,
  });

  // Level setting

  const contentIsVideo = [
    contentTypes.INTERACTIVE_VIDEO,
    contentTypes.GENERATION_GENIUS_VIDEO,
  ].includes(content?.content_type);

  const isEligibleForJRRLV4 =
    isStudent && isRecommendedArticleLevelExperimentOn && !contentIsVideo;

  const [currentLevel = {}, setCurrentLevel] = useUpdateLevel(
    content?.data.article_levels || [],
  );

  const skipRecommendedArticleLevelFetch = !isEligibleForJRRLV4;

  const { data: studentRecommendedArticleLevel } =
    useRecommendedArticleLevelGet(
      {
        contentId,
        language: EN_LANG_ATTRIBUTE_TAG,
        user,
        content,
      },
      skipRecommendedArticleLevelFetch,
    );

  const skipLegacyJRRLFetch = isEligibleForJRRLV4 || !isStudent;

  const { data: legacyStudentReadingLevel } = useReadingLevelGet(
    {},
    skipLegacyJRRLFetch,
  );

  const studentReadingLevel = isEligibleForJRRLV4
    ? studentRecommendedArticleLevel?.recommendation?.[0]
    : legacyStudentReadingLevel;

  const { isLevelLocked, unlockLevel } = useLevelLock(
    content,
    publishedUserAssignments,
    user,
    studentReadingLevel,
  );
  useSetInitialLevel(
    {
      content,
      publishedUserAssignments,
      setCurrentLevel,
      user,
      studentReadingLevel,
    },
    [redirectUrl],
  );

  // Booleans and page variables
  const contentMetadataUnavailable =
    contentMetadataLoading || contentMetadataRequestError;
  const currentAssignment = isStudent
    ? selectedAssignment || latestAssignment
    : selectedAssignment;
  const currentAssignmentId = currentAssignment?.content_id;
  const initialActivitySelections =
    selectedAssignment?.assignment_activity_selection?.selected_activities ??
    {};
  const hasHeaderImage = articleHasHeaderImage(content?.data);
  const shouldShowMobileActions = useShouldShowMobileActions(isStudent);
  const shouldShowPoll = useShouldShowPoll(
    isBannerModeActive,
    contentIsVideo,
    userProperties,
  );

  useEffect(() => {
    const currentLevelTitle = currentLevel?.title ?? currentLevel?.short_title;
    if (currentLevelTitle) {
      document.title = currentLevelTitle;
    }
  }, [levelId]);

  const liteUserPremiumContent = isLiteUser && content?.data?.is_premium;
  const userCanSeeKeyTerms = !liteUserPremiumContent;

  // Toggling key terms visibility
  const { isVocabularySupportEnabled, toggleVocabularySupport } =
    useVocabularySupport(
      userCanSeeKeyTerms,
      userProperties,
      currentAssignment,
      contentMetadataUnavailable ? undefined : contentMetadata,
    );
  const { keyTermData, rawText } = useKeyTerms(
    currentLevel?.raw_text,
    isVocabularySupportEnabled,
  );

  const hasLoggedLevelIdWarning = useRef(false);

  useEffect(() => {
    const isPageFullyLoaded =
      content && !assignmentsLoading && !contentMetadataLoading && currentLevel;

    if (
      isPageFullyLoaded &&
      !levelId &&
      currentLevel &&
      !hasLoggedLevelIdWarning.current
    ) {
      logger.warn({
        message: "View page loaded without levelId in URL query parameter",
        context: {
          contentId,
          articleHeaderId: content?.header_id,
          currentLevelId: currentLevel?.id,
          userId: user?.id,
          userUniqueId: user?.unique_id,
          assignmentId: currentAssignment?.assignment_id,
          matchedLegacyArticleLevelId: matchedLegacyArticleLevel?.id,
          url: router.asPath,
          contentType: content?.content_type,
          referrer: document?.referrer,
        },
      });
      hasLoggedLevelIdWarning.current = true;
    }
  }, [
    content,
    assignmentsLoading,
    contentMetadataLoading,
    currentLevel,
    levelId,
  ]);

  return (
    <ViewPageProviders
      content={content}
      contentId={contentId}
      contentType={content?.content_type}
      isBannerModeActive={isBannerModeActive}
      isLiteContent={content?.data?.is_lite}
      isPremiumContent={content?.data?.is_premium}
      currentAssignment={currentAssignment}
      currentLevel={currentLevel}
      currentLevelLanguage={currentLevel?.language}
      legacyArticleSlug={matchedLegacyArticleLevel?.slug}
      levelId={currentLevel?.id || levelId}
      rawText={rawText}
      selectedAssignmentId={selectedAssignmentId}
      setIsActivityPanelExpanded={setIsRightPanelExpanded}
      userProperties={userProperties}
      initialActivitySelections={initialActivitySelections}
    >
      <UnlockOrPreviewContentBannerForViewVeiledTop
        isPremiumContent={content?.data?.is_premium}
        isVideo={contentIsVideo}
      />
      {/* TODO: Delete once CAT advisories are deprecated for BTS 2023. */}
      {showContentAdvisoryBanner && <ContentAdvisoryBanner />}
      {isBannerModeActive && (
        <ViewPageBanner
          content={content}
          toggleBannerMode={toggleBannerMode}
          ref={bannerRef}
        />
      )}
      <ContentLayout>
        <Global styles={globalStyleOverrideCss(shouldShowMobileActions)} />
        {/* The controls only appear when read aloud mode is active. */}
        <ContentLayout.StickyBarContainer overrideToTop>
          <ReadAloudControls
            contentData={content?.data}
            contentId={contentId}
            currentLevel={currentLevel}
            headerContentId={matchedLegacyArticleLevel?.header_content_id}
            nextFocusRef={
              shouldShowMobileActions
                ? mobileActionsToolbarDropdownRef
                : readAloudButtonRef
            }
          />
        </ContentLayout.StickyBarContainer>
        <ContentLayout.Header>
          {isStudentAssignmentEmbedActive ? (
            <AssignmentEmbedContainer
              content={content}
              currentContentLevel={currentLevel}
              fetchAssignment={fetchAssignment}
              hasMultipleAssignments={publishedUserAssignments.length > 1}
              latestAssignment={latestAssignment}
              selectedAssignment={selectedAssignment}
              isSelectedAssignmentLoading={isSelectedAssignmentLoading}
            />
          ) : null}
          <ContentHeader
            assignment={currentAssignment}
            contentData={content}
            contentMetadata={
              contentMetadataUnavailable ? undefined : contentMetadata
            }
            currentLevel={currentLevel}
            publishedUserAssignments={publishedUserAssignments}
            userProperties={userProperties}
          />
        </ContentLayout.Header>
        <ContentLayout.StickyBarContainer>
          <AssignActionsContainer
            bannerMode={bannerMode}
            content={content}
            currentAssignment={currentAssignment}
            currentAssignmentId={currentAssignmentId}
            currentLevel={currentLevel}
            isLiteContent={content?.data?.is_lite}
            isLevelLocked={isLevelLocked}
            matchedAssignments={publishedUserAssignments}
            mobileActionsToolbarDropdownRef={mobileActionsToolbarDropdownRef}
            originalAssignment={currentAssignment}
            setCurrentLevel={setCurrentLevel}
            toggleVocabularySupport={toggleVocabularySupport}
            user={user}
            userProperties={userProperties}
          />
          <ContentSettingsAdvisory
            isStudent={isStudent}
            visibilityMarkers={content?.visibility_markers}
          />
        </ContentLayout.StickyBarContainer>
        <ContentLayout.Alert>
          <LevelAlert levels={content?.data.article_levels} />
        </ContentLayout.Alert>
        <ContentLayout.Panel
          contentIsVideo={contentIsVideo}
          hasHeaderImage={hasHeaderImage}
          isExpanded={isRightPanelExpanded}
        >
          <ActivityPanel
            assignments={publishedUserAssignments}
            assignmentsLoading={assignmentsLoading}
            bannerMode={bannerMode}
            content={content}
            contentMetadata={
              contentMetadataUnavailable ? undefined : contentMetadata
            }
            contentId={contentId}
            contentType={content?.content_type}
            currentLevel={currentLevel}
            currentAssignment={currentAssignment}
            fetchAssignment={fetchAssignment}
            isBannerModeActive={isBannerModeActive}
            isExpanded={isRightPanelExpanded}
            isLevelLocked={isLevelLocked}
            isSelectedAssignmentLoading={isSelectedAssignmentLoading}
            latestAssignment={latestAssignment}
            legacyArticleLevelData={matchedLegacyArticleLevel}
            mobileActionsToolbarDropdownRef={mobileActionsToolbarDropdownRef}
            quizOmissionReason={content?.data?.quiz_omission_reason}
            selectedAssignment={currentAssignment}
            setIsExpanded={setIsRightPanelExpanded}
            levels={content?.data?.article_levels}
            studentId={user?.student?.id}
            toggleVocabularySupport={toggleVocabularySupport}
            unlockLevel={unlockLevel}
            user={user}
            userProperties={userProperties}
          />
        </ContentLayout.Panel>
        <ContentLayout.MainSection
          contentIsVideo={contentIsVideo}
          hasHeaderImage={hasHeaderImage}
          isRightPanelExpanded={isRightPanelExpanded}
        >
          {shouldShowPoll && (
            <ContentLayout.PollSection>
              <PollContainer
                contentType={content?.content_type}
                currentContentLevelId={currentLevel?.id}
                isStudentAssignmentEmbedActive={isStudentAssignmentEmbedActive}
                isPremiumContent={content?.data?.is_premium}
                levelId={currentLevel.id}
                openAssignmentEditor={() =>
                  setIsStudentAssignmentEmbedActive(true)
                }
                selectedAssignmentId={selectedAssignmentId}
                userId={user.unique_id}
                userProperties={userProperties}
              />
            </ContentLayout.PollSection>
          )}

          <ContentDisplay
            bannerMode={bannerMode}
            content={content}
            contentMetadata={
              contentMetadataUnavailable ? undefined : contentMetadata
            }
            currentLevel={currentLevel}
            isVocabularySupportEnabled={isVocabularySupportEnabled}
            keyTermDataContentId={keyTermData?.content_id}
            legacyArticleId={matchedLegacyArticleLevel?.id}
            matchedAssignments={publishedUserAssignments}
            rawText={rawText}
            readAloudButtonRef={readAloudButtonRef}
            shouldShowMobileActions={shouldShowMobileActions}
            toggleVocabularySupport={toggleVocabularySupport}
            user={user}
          />
          <UnlockOrPreviewContentBannerForViewVeiledBottom
            isPremiumContent={content?.data?.is_premium}
            isVideo={contentIsVideo}
          />
        </ContentLayout.MainSection>
        <ContentLayout.PageBottom>
          <RelatedContent
            content={content}
            contentMetadata={contentMetadata}
            legacyArticleLevel={matchedLegacyArticleLevel}
            userAssignments={publishedUserAssignments}
            userProperties={userProperties}
          />
          <ViewPageFooter />
        </ContentLayout.PageBottom>
      </ContentLayout>
    </ViewPageProviders>
  );
};

ViewPageContent.propTypes = propTypes;

export default ViewPageContent;
